import i18n from "src/locales";
import { captureException } from "src/utils/error";
import saveToBraze from "src/utils/brazeUploader";
import getStore from "src/utils/redux/store";
import { trackEvent } from "src/utils/api/tracker";
import { updatePaymentEnrollmentFormState } from "src/utils/redux/slices/paymentEnrollmentForm";
import { hasEnrollmentField } from "src/utils/enrollmentFields";
import { getRouteId } from "src/utils/meristemContext";

export interface SmsConsentUserPayload {
  phoneNumber: string;
  consentForTelehealthSms: boolean;
  consentForMarketingSms: boolean;
}

// Shape handed to crm-uploader-lambda's ATTRIBUTE_MAP.
export function buildSmsConsentUserPayload(input: {
  phoneNumber: string | undefined;
  marketingSmsConsent: boolean | undefined;
}): SmsConsentUserPayload {
  return {
    phoneNumber: (input.phoneNumber || "").trim(),
    consentForTelehealthSms: true,
    consentForMarketingSms: !!input.marketingSmsConsent,
  };
}

// Records the user's current SMS consent state across Redux, Iterable, and
// Mixpanel. Called on Continue past the consent disclaimer where there is an
// account accordion (captures consent for users who abandon before signup), on
// OnSignedUp (reflects final state), and directly before createPatientData() in
// the accordion-less telehealth checkouts, which need Redux seeded before the
// consent payload is built. All call sites read live Redux, so the last one
// wins for state — but each emits its own event.
export function maybeRecordSmsConsent(): void {
  try {
    // Some flows collect a phone number without ever rendering the SMS
    // disclaimer, and shop hides it again for users whose consent is already on
    // file. Recording there would log agreement to copy nobody was shown, and
    // would overwrite an earlier opt-out.
    if (!hasEnrollmentField("smsConsent")) return;

    const store = getStore();
    const enrollmentInfo =
      store.getState()?.paymentEnrollmentForm?.enrollmentInfo;
    if (!enrollmentInfo?.phoneNumber) return;

    if (!enrollmentInfo.smsConsent) {
      store.dispatch(
        updatePaymentEnrollmentFormState({
          enrollmentInfo: { ...enrollmentInfo, smsConsent: true },
        })
      );
    }

    const user = buildSmsConsentUserPayload({
      phoneNumber: enrollmentInfo.phoneNumber,
      marketingSmsConsent: enrollmentInfo.marketingSmsConsent,
    });

    saveToBraze({ user });

    const routeId = getRouteId();
    const pageSet = getStore().getState().linearBuyflow.pageSetName;

    trackEvent("SuccessfullySavedSmsConsentForUser", {
      copy: i18n.t("payment:smsTransactionalDisclaimer"),
      consentType: "transactional",
    });
    saveToBraze({
      event: {
        event_name: "SuccessfullySavedSmsConsentForUser",
        event_data: {
          consentType: "transactional",
          copy: i18n.t("payment:smsTransactionalDisclaimer"),
          routeId,
          pageSet,
        },
      },
    });

    if (user.consentForMarketingSms) {
      trackEvent("SuccessfullySavedSmsConsentForUser", {
        copy: i18n.t("enrollmentForm:inputFields:marketingSmsConsent:label"),
        consentType: "marketing",
      });
      saveToBraze({
        event: {
          event_name: "SuccessfullySavedSmsConsentForUser",
          event_data: {
            consentType: "marketing",
            copy: i18n.t(
              "enrollmentForm:inputFields:marketingSmsConsent:label"
            ),
            routeId,
            pageSet,
          },
        },
      });
    }
  } catch (e) {
    captureException(e);
  }
}
